import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { RealtimeEvent } from '@fas/types';
import { api, type QueryParams } from '@/lib/api';
import { useRealtimeRefresh } from '@/lib/realtime';

/** Paginated attendance list; keys start with '/attendance' so live refreshes reach them. */
export function useAttendancePage<T>(path: string, params: QueryParams, enabled = true) {
  return useQuery({
    queryKey: ['/attendance', path, params],
    queryFn: ({ signal }) => api.getPage<T>(path, params, signal),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** Current time, re-rendering every `intervalMs` (for "now / next" labels and clock windows). */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Non-sensitive settings readable before sign-in, e.g. the GPS disclosure text (§14.4). */
export function usePublicSettings() {
  return useQuery({
    queryKey: ['/settings/public'],
    queryFn: ({ signal }) => api.get<Record<string, unknown>>('/settings/public', undefined, signal),
    staleTime: 10 * 60_000,
  });
}

const ATTENDANCE_EVENTS = [
  RealtimeEvent.ATTENDANCE_CLOCKED_IN,
  RealtimeEvent.ATTENDANCE_CLOCKED_OUT,
  RealtimeEvent.ATTENDANCE_FLAGGED,
  RealtimeEvent.ATTENDANCE_VERIFIED,
  RealtimeEvent.ATTENDANCE_APPROVED,
  RealtimeEvent.ATTENDANCE_REJECTED,
  RealtimeEvent.ATTENDANCE_CORRECTED,
];

/** Refetches attendance queries when the server announces a change (at most once per `delayMs`). */
export function useAttendanceLiveRefresh(delayMs = 1_500): void {
  const queryClient = useQueryClient();
  useRealtimeRefresh(ATTENDANCE_EVENTS, () => void queryClient.invalidateQueries({ queryKey: ['/attendance'] }), delayMs);
}
